import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { supabase } from "../lib/supabase";

/**
 * ResultScreen
 * Props: teamId, results[], onRestart, isSolo, searchProvider
 */
export default function ResultScreen({ teamId, results = [], onRestart, isSolo = false, searchProvider = 'naver' }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const confettiFired = useRef(false);

  const sorted = [...results].sort((a, b) => b.ok - a.ok || a.pass - b.pass);
  const winner = sorted[0];

  useEffect(() => {
    if (confettiFired.current) return;
    confettiFired.current = true;
    const end = Date.now() + 3000;
    const frame = () => {
      confetti({ particleCount:6, angle:60, spread:55, origin:{x:0,y:0.7}, colors:["#f97316","#facc15","#22c55e","#3b82f6","#ec4899"] });
      confetti({ particleCount:6, angle:120, spread:55, origin:{x:1,y:0.7}, colors:["#f97316","#facc15","#22c55e","#3b82f6","#ec4899"] });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  const handleSave = async () => {
    if (!winner || saving || saved) return;
    if (isSolo || !teamId) { setSaved(true); return; }
    setSaving(true); setSaveError(null);
    const { error } = await supabase.from("history").insert({ team_id: teamId, menu_name: winner.name, eaten_at: new Date().toISOString().slice(0,10) });
    setSaving(false);
    if (error) setSaveError("저장 실패. 다시 시도해 주세요.");
    else setSaved(true);
  };

  const getMapUrl = (name) => searchProvider === 'kakao'
    ? `https://map.kakao.com/?q=${encodeURIComponent(name)}`
    : `https://map.naver.com/v5/search/${encodeURIComponent(name)}`;

  const handleShareResult = async () => {
    const text = `🍽️ 오늘의 점심은 "${winner.name}"! (런치픽으로 결정했어요)`;
    if (navigator.share) {
      try { await navigator.share({ title: '런치픽 결과', text }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(text); } catch {}
    }
  };

  if (!winner) {
    return <div style={S.bg}><p style={{color:"var(--text-muted)",fontSize:"16px"}}>결과가 없습니다.</p></div>;
  }

  return (
    <div style={S.bg}>
      <div style={S.inner}>
        {/* 1등 카드 */}
        <div style={S.winnerCard}>
          <span style={S.trophy}>🏆</span>
          <p style={S.winnerLabel}>{isSolo ? '오늘의 선택은' : '오늘의 점심은'}</p>
          <h1 style={S.winnerName}>{winner.name}!</h1>
          <VoteBar ok={winner.ok} pass={winner.pass} large />
        </div>

        {/* 전체 순위 */}
        {sorted.length > 1 && (
          <div style={S.rankSection}>
            <h3 style={S.rankTitle}>전체 순위</h3>
            <ol style={S.rankList}>
              {sorted.map((menu, idx) => (
                <li key={menu.name} style={S.rankItem}>
                  <span style={{...S.rankIdx, ...(idx===0?S.rankIdxFirst:{})}}>{idx+1}</span>
                  <div style={S.rankContent}>
                    <span style={S.rankName}>{menu.name}</span>
                    <VoteBar ok={menu.ok} pass={menu.pass} />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* 지도 링크 (프로바이더에 따라) */}
        <a href={getMapUrl(winner.name)} target="_blank" rel="noopener noreferrer" style={S.mapLink}>
          🗺️ {searchProvider === 'kakao' ? '카카오맵' : '네이버 지도'}에서 보기
        </a>

        {/* 결과 공유 버튼 */}
        <button className="btn-outline" onClick={handleShareResult} style={{marginBottom:0}}>
          📤 결과 공유하기
        </button>

        {/* 버튼 영역 */}
        <div style={S.btnRow}>
          <button style={S.restartBtn} onClick={onRestart}>🔄 다시 투표하기</button>
          <button style={{...S.saveBtn, opacity: saved||saving ? 0.6 : 1, cursor: saved||saving ? "default" : "pointer"}}
            onClick={handleSave} disabled={saved||saving}>
            {saving ? "저장 중…" : saved ? "✅ 저장됨" : "📌 오늘 메뉴로 저장"}
          </button>
        </div>
        {saveError && <p style={S.errText}>{saveError}</p>}
      </div>
    </div>
  );
}

function VoteBar({ ok, pass, large = false }) {
  const total = ok + pass || 1;
  const okPct = Math.round((ok / total) * 100);
  const passPct = 100 - okPct;
  return (
    <div style={{width:"100%",marginTop:large?"16px":"8px"}}>
      <div style={B.labels}>
        <span style={{...B.label,color:"var(--accent-green)"}}>👍 OK {ok}표</span>
        <span style={{...B.label,color:"var(--accent-red)"}}>👎 PASS {pass}표</span>
      </div>
      <div style={{...B.track,height:large?"14px":"10px"}}>
        {okPct>0 && <div style={{...B.okFill,width:`${okPct}%`,height:"100%",borderRadius:passPct===0?"var(--radius-full)":"999px 0 0 999px"}} />}
        {passPct>0 && <div style={{...B.passFill,width:`${passPct}%`,height:"100%",borderRadius:okPct===0?"var(--radius-full)":"0 999px 999px 0"}} />}
      </div>
      <div style={B.pctRow}>
        <span style={{color:"var(--accent-green)",fontSize:"12px",fontWeight:600}}>{okPct}%</span>
        <span style={{color:"var(--accent-red)",fontSize:"12px",fontWeight:600}}>{passPct}%</span>
      </div>
    </div>
  );
}

const S = {
  bg:{minHeight:"100dvh",background:"var(--bg-gradient)",display:"flex",justifyContent:"center",padding:"32px 16px 48px",fontFamily:"var(--font-family)"},
  inner:{width:"100%",maxWidth:"440px",display:"flex",flexDirection:"column",gap:"16px",animation:"fadeInUp 0.5s ease"},
  winnerCard:{display:"flex",flexDirection:"column",alignItems:"center",padding:"32px 28px 28px",border:"2px solid rgba(245,158,11,0.4)",borderRadius:"var(--radius-xl)",background:"linear-gradient(145deg, rgba(245,158,11,0.12) 0%, rgba(255,255,255,0.05) 100%)",boxShadow:"0 6px 32px rgba(245,158,11,0.15)",textAlign:"center"},
  trophy:{fontSize:"64px",lineHeight:1,marginBottom:"8px",filter:"drop-shadow(0 4px 6px rgba(0,0,0,0.3))"},
  winnerLabel:{margin:"0 0 4px",fontSize:"15px",color:"var(--accent-amber)",fontWeight:600},
  winnerName:{margin:0,fontSize:"clamp(28px,7vw,42px)",fontWeight:"900",color:"var(--text-primary)",letterSpacing:"-0.5px"},
  rankSection:{backgroundColor:"var(--bg-card)",border:"1px solid var(--border-card)",borderRadius:"var(--radius-lg)",padding:"20px 20px 16px"},
  rankTitle:{margin:"0 0 16px",fontSize:"15px",fontWeight:"700",color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"1px"},
  rankList:{margin:0,padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:"16px"},
  rankItem:{display:"flex",alignItems:"flex-start",gap:"12px"},
  rankIdx:{flexShrink:0,width:"28px",height:"28px",borderRadius:"50%",backgroundColor:"rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"13px",fontWeight:"800",color:"var(--text-secondary)",marginTop:"2px"},
  rankIdxFirst:{background:"var(--accent-gradient)",color:"#fff"},
  rankContent:{flex:1,display:"flex",flexDirection:"column",gap:"2px"},
  rankName:{fontSize:"15px",fontWeight:"700",color:"var(--text-primary)"},
  mapLink:{display:"block",textAlign:"center",padding:"12px",background:"rgba(3,199,90,0.1)",border:"1px solid rgba(3,199,90,0.25)",borderRadius:"var(--radius-md)",color:"#4ade80",fontSize:"14px",fontWeight:"600",textDecoration:"none"},
  btnRow:{display:"flex",flexDirection:"column",gap:"12px"},
  restartBtn:{width:"100%",padding:"16px",fontSize:"16px",fontWeight:"700",fontFamily:"var(--font-family)",backgroundColor:"rgba(255,255,255,0.07)",border:"1px solid var(--border-card)",color:"var(--text-secondary)",borderRadius:"var(--radius-md)",cursor:"pointer"},
  saveBtn:{width:"100%",padding:"16px",fontSize:"16px",fontWeight:"700",fontFamily:"var(--font-family)",background:"var(--accent-gradient)",color:"#fff",border:"none",borderRadius:"var(--radius-md)",cursor:"pointer",boxShadow:"var(--shadow-glow-orange)"},
  errText:{textAlign:"center",color:"var(--accent-red)",fontSize:"13px",margin:0},
};
const B = {
  labels:{display:"flex",justifyContent:"space-between",marginBottom:"4px"},
  label:{fontSize:"12px",fontWeight:"600"},
  track:{display:"flex",width:"100%",backgroundColor:"rgba(255,255,255,0.1)",borderRadius:"var(--radius-full)",overflow:"hidden"},
  okFill:{backgroundColor:"var(--accent-green)",transition:"width 0.6s ease"},
  passFill:{backgroundColor:"var(--accent-red)",transition:"width 0.6s ease"},
  pctRow:{display:"flex",justifyContent:"space-between",marginTop:"3px"},
};
